<script setup lang="ts">
import SancareTabs, { SancareTabItem } from '@sancare/typescript-commons/src/design-system/molecules/SancareTabs.vue'
import dayjs from '@sancare/typescript-commons/src/misc/dayjs'
import { getDrugEventPrestationTypeLabel, getDrugEventPrestationTypes } from '@sancare/typescript-commons/src/misc/DrugEventHelper'
import { DrugEventPrestationType, DrugEventType, HealthDataDocument, HealthDataType } from '@sancare/typescript-commons/src/types/health-data'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { mapDrugDocumentItem, mapHealthConstantDocumentItem, mapLabResultDocumentItem, mapTextualLabResultDocumentItem } from '@/misc/stayDocuments'
import { DrugDocumentItem, EvaluationFeedbackActions, EvaluationFeedbackVerdict, EvaluationItem, EvaluationVerdict, FlagJustificationPayload, HealthConstantDocumentItem, LabResultDocumentItem, SuggestionJustification, TextualLabResultDocumentItem } from '@/misc/types'
import { StayHealthData } from '@/store/modules/stay/types'

import EvaluationPanel from './EvaluationPanel.vue'
import { buildTextualPart } from './justification-parts'
import TextualPartsList from './TextualPartsList.vue'

const { t } = useI18n()

const props = withDefaults(defineProps<{
  isLoading?: boolean
  noValueLabel?: string | null
  justification?: SuggestionJustification | null
  // Only falseJustifications is actually read here — narrowed from the full StayHealthData so a
  // caller that hasn't loaded the whole stay (e.g. the staylist's aside quick view) can provide
  // this popin's data without faking the rest of it.
  healthData: Pick<StayHealthData, 'falseJustifications'>
  // Identity of the suggestion this popin is showing (e.g. type: 'DAS', reference: 'I210'), known
  // unambiguously by the caller — see ResolveFalseJustificationOriginUseCase on the backend. Signalling
  // icons only show up when both are provided.
  labelType?: string | null
  labelReference?: string | null
  // Lets the user give their feedback on the evaluations (needs the stay loaded in the store)
  feedbackActions?: EvaluationFeedbackActions | null
}>(), {
  isLoading: false,
  noValueLabel: null,
  justification: null,
  labelType: null,
  labelReference: null,
  feedbackActions: null,
})

const emit = defineEmits<{
  // event is the native click that triggered the selection (absent when programmatic) — callers that
  // navigate away instead of updating local state (the aside quick view) use it to open the target in
  // a new tab on ctrl/middle click, mirroring redirectToStayDisplayer.
  'select-health-data': [mainContent: HealthDataDocument, scrollTarget?: { docId: number, offset: number | null }, event?: MouseEvent]
  'flag-justification': [payload: FlagJustificationPayload]
  'unflag-justification': [payload: { justificationId: number, docType: string, docId: number }]
}>()

const canFlag = computed(() => props.labelType !== null && props.labelReference !== null)

const hasDocumentsContent = computed(() => Boolean(
  props.justification
  && (props.justification.textualJustifications.length || props.justification.structuredJustifications.length)
))

const evaluations = computed<EvaluationItem[]>(() => props.justification?.evaluations ?? [])

const hasContent = computed(() => hasDocumentsContent.value || evaluations.value.length > 0)

const TAB_DOCUMENTS = 'documents'

const VERDICT_TAB_STATUSES: Record<EvaluationVerdict, SancareTabItem['status']> = { accepted: null, rejected: 'danger', uncertain: 'warning' }
const FEEDBACK_VERDICT_TAB_DOTS: Record<EvaluationFeedbackVerdict, SancareTabItem['dot']> = { agree: 'primary', disagree: 'danger', debatable: 'warning' }
// From the lowest verdict to the highest
const FEEDBACK_VERDICTS_ORDER: EvaluationFeedbackVerdict[] = ['disagree', 'debatable', 'agree']

// The lowest verdict of the users' feedbacks on the evaluation, none without feedback
function lowestFeedbackVerdict(evaluation: EvaluationItem): EvaluationFeedbackVerdict | null {
  return FEEDBACK_VERDICTS_ORDER.find((verdict) => evaluation.feedbacks.some((feedback) => feedback.verdict === verdict)) ?? null
}

function evaluationTabKey(evaluation: EvaluationItem): string {
  return `evaluation-${evaluation.id}`
}

// A user's evaluation is labelled with their name alone, its icon already telling it apart
function evaluationTabLabel(evaluation: EvaluationItem): string {
  const prefix = evaluation.authorType === 'model' && evaluation.modelType ? t(`prediction.modelType.${evaluation.modelType}`) : ''
  return `${prefix} ${evaluation.authorName}`.trim()
}

function evaluationTabItem(evaluation: EvaluationItem): SancareTabItem {
  const feedbackVerdict = lowestFeedbackVerdict(evaluation)
  return {
    key: evaluationTabKey(evaluation),
    label: evaluationTabLabel(evaluation),
    icon: evaluation.authorType === 'user' ? 'person' : 'robot',
    status: VERDICT_TAB_STATUSES[evaluation.verdict],
    dot: feedbackVerdict ? FEEDBACK_VERDICT_TAB_DOTS[feedbackVerdict] : null,
    dotTooltip: feedbackVerdict
      ? t(`stayDisplayer.justificationSummary.evaluation.feedbackDotTooltip.${feedbackVerdict}`, {
        // "the agent" for a model, the user's name otherwise, as in the evaluation tab
        author: evaluation.authorType === 'user' ? evaluation.authorName : t('stayDisplayer.justificationSummary.evaluation.agentObject'),
      })
      : null,
  }
}

// Each tab only shows up when it actually has something to display : the evaluations written by
// users come first, then the documents, then the evaluations of the prediction models.
const tabItems = computed<SancareTabItem[]>(() => [
  ...evaluations.value.filter((evaluation) => evaluation.authorType === 'user').map(evaluationTabItem),
  ...(hasDocumentsContent.value ? [{ key: TAB_DOCUMENTS, label: t('stayDisplayer.justificationSummary.documentsTab'), icon: 'search' }] : []),
  ...evaluations.value.filter((evaluation) => evaluation.authorType !== 'user').map(evaluationTabItem),
])

// Documents by default, unless there is nothing to show there : then the first tab.
function defaultTab(): string {
  return hasDocumentsContent.value ? TAB_DOCUMENTS : (tabItems.value[0]?.key ?? TAB_DOCUMENTS)
}

const activeTab = ref<string>(defaultTab())

// Reset to the default tab whenever the popin starts showing a different suggestion's justification,
// so a previously selected tab doesn't bleed into the next one.
watch(() => props.justification, () => {
  activeTab.value = defaultTab()
})

const textualParts = computed(() => (props.justification?.textualJustifications ?? [])
  .map((just) => buildTextualPart(just, props.healthData.falseJustifications ?? [], t('stayDisplayer.justificationSummary.untitledDocument')))
  .filter((part) => part !== null))

// creationDate comes back as a raw ISO string on the enriched /justifications structured values
// (same wire shape as /documents, see stayDocuments.ts) — parsed to Dayjs here since this bypasses
// simpleParseStay (the usual place that conversion happens for the rest of the stay).
function withDayjsCreationDate<T extends { creationDate: string | null }>(item: T): Omit<T, 'creationDate'> & { creationDate: dayjs.Dayjs | null } {
  return { ...item, creationDate: item.creationDate ? dayjs(item.creationDate) : null }
}

function structuredValuesOf(sourceType: string) {
  if (!props.justification) {
    return []
  }
  return props.justification.structuredJustifications
    .filter((j) => j.sourceType === sourceType && j.value !== null)
    .map((j) => j.value!)
}

const drugEvents = computed(() => structuredValuesOf('drugEvent').map((v) => withDayjsCreationDate(mapDrugDocumentItem(v as DrugDocumentItem))))
const drugEventPrestationTypes = computed(() => getDrugEventPrestationTypes(drugEvents.value))
const healthConstants = computed(() => structuredValuesOf('healthConstant').map((v) => withDayjsCreationDate(mapHealthConstantDocumentItem(v as HealthConstantDocumentItem))))
const labResults = computed(() => structuredValuesOf('labResult').map((v) => withDayjsCreationDate(mapLabResultDocumentItem(v as LabResultDocumentItem))))
const categoricalLabResults = computed(() => structuredValuesOf('categoricalLabResult').map((v) => withDayjsCreationDate(mapTextualLabResultDocumentItem(v as TextualLabResultDocumentItem))))

function handleSectionSelection(type: HealthDataType, drugEventPrestationType?: DrugEventPrestationType, event?: MouseEvent) {
  emit('select-health-data', { type, targetId: null, drugEventPrestationType }, undefined, event)
}
</script>

<template>
  <div class="justification-popover">
    <div
      v-if="!isLoading && hasContent"
      class="tabbed-justification"
    >
      <sancare-tabs
        v-model="activeTab"
        :items="tabItems"
        variant="pills"
        class="p-3"
      />
      <div class="tab-content">
        <div
          v-for="evaluation in evaluations"
          :key="evaluation.id"
          class="tab-panel"
          :class="{ 'tab-panel--hidden': activeTab !== evaluationTabKey(evaluation) }"
          :aria-hidden="activeTab !== evaluationTabKey(evaluation)"
        >
          <evaluation-panel
            :evaluation="evaluation"
            :false-justifications="healthData.falseJustifications ?? []"
            :feedback-actions="feedbackActions"
            @select-health-data="(mainContent, scrollTarget, event) => emit('select-health-data', mainContent, scrollTarget, event)"
          />
        </div>
        <div
          class="tab-panel tab-panel--documents"
          :class="{ 'tab-panel--hidden': activeTab !== TAB_DOCUMENTS }"
          :aria-hidden="activeTab !== TAB_DOCUMENTS"
        >
          <textual-parts-list
            :parts="textualParts"
            :can-flag="canFlag"
            :label-type="labelType"
            :label-reference="labelReference"
            @select-health-data="(mainContent, scrollTarget, event) => emit('select-health-data', mainContent, scrollTarget, event)"
            @flag-justification="emit('flag-justification', $event)"
            @unflag-justification="emit('unflag-justification', $event)"
          />
          <div
            v-if="drugEvents.length"
            class="justification-part"
          >
            <template
              v-for="type in drugEventPrestationTypes"
              :key="type"
            >
              <h3
                class="btn btn-sm btn-report-select"
                @click="handleSectionSelection(HealthDataType.DRUG_EVENT, type, $event)"
                @click.middle="handleSectionSelection(HealthDataType.DRUG_EVENT, type, $event)"
              >
                {{ getDrugEventPrestationTypeLabel(type) }}
              </h3>
              <div
                v-for="event in drugEvents.filter((drugEvent) => drugEvent.prestationType === type)"
                :key="event.id"
                class="justification-part-line"
              >
                <div class="row mx-2 my-1 align-items-center">
                  <span class="col-2">{{ event.creationDate ? event.creationDate.format('L') : '-' }}</span>
                  <div class="col hinted-summary-line">
                    <div class="container py-2">
                      <span class="row">{{ DrugEventType[event.type] }}</span>
                      <span class="row">{{ event.drug.atc }}</span>
                      <span class="row">{{ event.drug.commercialName }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </div>
          <div
            v-if="healthConstants.length"
            class="justification-part"
          >
            <h3
              class="btn btn-sm btn-report-select"
              @click="handleSectionSelection(HealthDataType.HEALTH_CONSTANT, undefined, $event)"
              @click.middle="handleSectionSelection(HealthDataType.HEALTH_CONSTANT, undefined, $event)"
            >
              {{ t('stayDisplayer.justificationSummary.constants') }}
            </h3>
            <div
              v-for="(constant, healthIdx) in healthConstants"
              :key="constant.id"
              class="justification-part-line"
            >
              <hr v-if="healthIdx > 0">
              <div class="row mx-1 popover-report-content align-items-center">
                <span class="col-3">{{ constant.creationDate ? constant.creationDate.format('L') : '-' }}</span>
                <div class="hinted-summary-line">
                  <span class="col-6">{{ constant.constantType.description }}</span>
                  <span class="col-3">{{ constant.value }}</span>
                </div>
              </div>
            </div>
          </div>
          <div
            v-if="labResults.length || categoricalLabResults.length"
            class="justification-part"
          >
            <h3
              class="btn btn-sm btn-report-select"
              @click="handleSectionSelection(HealthDataType.LAB_RESULT, undefined, $event)"
              @click.middle="handleSectionSelection(HealthDataType.LAB_RESULT, undefined, $event)"
            >
              {{ t('stayDisplayer.justificationSummary.labResults') }}
            </h3>
            <div
              v-for="result in labResults"
              :key="result.id"
              class="justification-part-line"
            >
              <div class="row mx-1 popover-report-content align-items-center">
                <span class="col-3">{{ result.creationDate ? result.creationDate.format('L') : '-' }}</span>
                <div class="hinted-summary-line">
                  <span class="col-4">{{ result.title }}</span>
                  <span class="col-2">{{ result.code }}</span>
                  <span class="col-3">{{ result.value }}</span>
                </div>
              </div>
            </div>
            <div
              v-for="result in categoricalLabResults"
              :key="result.id"
              class="justification-part-line"
            >
              <div class="row mx-1 popover-report-content align-items-center">
                <span class="col-3">{{ result.creationDate ? result.creationDate.format('L') : '-' }}</span>
                <div class="hinted-summary-line">
                  <span class="col-4">{{ result.title }}</span>
                  <span class="col-2">{{ result.code }}</span>
                  <span class="col-3">{{ result.value }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else-if="isLoading">
      <div class="loader loader-center mb-4" />
      <p class="">
        {{ t('stayDisplayer.justificationSummary.loading') }}
      </p>
    </div>
    <div v-else>
      <p class="justification-message">
        {{ noValueLabel ?? t('justification.noValue') }}
      </p>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@sancare/typescript-commons/src/assets/styles/variables';
@import '@/design-system/tokens/colors';

// The popin keeps the height of its tallest tab (capped to the screen) whatever the tab shown, so it
// never moves when switching tabs: all the panels are stacked in the same grid cell, the hidden ones
// still taking up room. Each panel scrolls on its own below the tab bar, a short one has no scroll.
// The 2rem leave room for the popover's offset from its anchor, so it never overflows the screen.

.tabbed-justification {
  display: flex;
  max-height: calc(100vh - 2rem);
  flex-direction: column;
}

.tab-content {
  display: grid;
  min-height: 0;
  flex: 1 1 auto;
  grid-template-rows: minmax(0, 1fr);
}

.tab-panel {
  overflow-y: auto;
  min-height: 0;
  grid-area: 1 / 1;
}

.tab-panel--hidden {
  visibility: hidden;
  pointer-events: none;
}

.nav-pills {
  background: $bg-primary-subtle;
}
</style>
